import { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useGamesStore } from '../../stores/gamesStore';
import {
  STATUS_LABELS,
  REJECTION_LABELS,
  formatNumber,
  formatPercent,
  formatDayLabel,
  formatDurationSeconds,
  PeriodSelector,
  MetricCard,
  EmptyChart,
  chartColors,
} from './shared';

const axisTick = { fontSize: 12 };

// ============================================
// Карточки охвата и экономики
// ============================================

const SummaryCards = ({ summary }) => {
  if (!summary) return null;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard
        title="Игроков"
        value={formatNumber(summary.players)}
        subValue={`${formatNumber(summary.runs)} партий за период`}
      />
      <MetricCard
        title="Завершено партий"
        value={formatNumber(summary.finished)}
        subValue={`из ${formatNumber(summary.runs)} начатых`}
      />
      <MetricCard
        title="Выдано звёзд"
        value={formatNumber(summary.stars)}
        subValue={`${summary.stars_per_run} на партию`}
        accent
      />
      <MetricCard
        title="Партий без звёзд"
        value={formatPercent(summary.zero_star_rate)}
        subValue={`${formatNumber(summary.zero_star_finished)} из ${formatNumber(summary.finished)}`}
      />
    </div>
  );
};

// ============================================
// Динамика по дням
// ============================================

const TimeseriesChart = ({ series }) => {
  const data = (series || []).map((point) => ({
    ...point,
    label: formatDayLabel(point.date),
  }));

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
      <h2 className="text-lg font-semibold text-text-primary mb-1">Динамика</h2>
      <p className="text-sm text-text-secondary mb-4">Партии и выданные звёзды по дням</p>

      {data.length === 0 ? (
        <EmptyChart hint="Никто не играл за выбранный период" />
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
            <XAxis dataKey="label" stroke={chartColors.axis} tick={axisTick} />
            <YAxis stroke={chartColors.axis} tick={axisTick} />
            <Tooltip
              contentStyle={chartColors.tooltip}
              labelFormatter={(value) => value}
              formatter={(value, name) =>
                name === 'runs' ? [`${value} партий`, 'Партии'] : [formatNumber(value), 'Звёзды']
              }
            />
            <Line
              type="monotone"
              dataKey="runs"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6', r: 3 }}
              name="runs"
            />
            <Line
              type="monotone"
              dataKey="stars"
              stroke="#22c55e"
              strokeWidth={2}
              dot={{ fill: '#22c55e', r: 3 }}
              name="stars"
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

// ============================================
// Срез по играм
// ============================================

const ByGameTable = ({ games }) => {
  if (!games || games.length === 0) return null;

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-color overflow-hidden">
      <div className="p-6 border-b border-border-color">
        <h2 className="text-lg font-semibold text-text-primary">По играм</h2>
        <p className="text-sm text-text-secondary">
          Средний счёт и длительность считаны по партиям с оценённым счётом
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-bg-tertiary border-b border-border-color">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Игра</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Партий</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Игроков</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Ср. счёт</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Ср. время</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Звёзд</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">1-й порог</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-color">
            {games.map((game) => {
              // Средний счёт ниже первого порога — значит игроки почти
              // никогда не дотягивают до минимальной награды. Помечаем
              // прямо в таблице: это главный сигнал о настройке наград.
              const scoreBelowFirstTier =
                game.avg_score !== null && game.first_tier !== null && game.avg_score < game.first_tier;

              return (
                <tr key={game.game_id} className="hover:bg-bg-tertiary/50 transition-colors">
                  <td className="px-4 py-3 text-sm text-text-primary">{game.title}</td>
                  <td className="px-4 py-3 text-sm text-text-primary text-right">{formatNumber(game.runs)}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary text-right">{formatNumber(game.players)}</td>
                  <td className="px-4 py-3 text-sm text-text-primary text-right">
                    <span className={scoreBelowFirstTier ? 'text-error' : ''}>
                      {game.avg_score === null ? '—' : formatNumber(game.avg_score)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary text-right whitespace-nowrap">
                    {formatDurationSeconds(game.avg_duration)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className={game.stars > 0 ? 'text-accent-primary font-medium' : 'text-text-muted'}>
                      {formatNumber(game.stars)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary text-right">
                    {game.first_tier === null ? '—' : formatNumber(game.first_tier)}
                    {scoreBelowFirstTier ? (
                      <div className="text-xs text-error mt-1">счёт ниже порога</div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// Исход партии
// ============================================

const OutcomesPanel = ({ outcomes, onResetStale, isResetting }) => {
  if (!outcomes) return null;

  const total = (outcomes.statuses || []).reduce((sum, row) => sum + row.c, 0);
  const stale = outcomes.stale_active || 0;

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
      <h2 className="text-lg font-semibold text-text-primary mb-1">Исход партии</h2>
      <p className="text-sm text-text-secondary mb-4">Чем заканчиваются попытки игроков</p>

      {total === 0 ? (
        <div className="py-8 text-center text-sm text-text-secondary">Нет данных за выбранный период</div>
      ) : (
        <>
          <div className="space-y-2">
            {(outcomes.statuses || []).map((row) => (
              <div key={row.status} className="flex items-center gap-3">
                <div className="w-28 shrink-0 text-sm text-text-secondary">
                  {STATUS_LABELS[row.status] || row.status}
                </div>
                <div className="flex-1 h-6 bg-bg-tertiary rounded overflow-hidden">
                  <div
                    className="h-full bg-accent-primary/60"
                    style={{ width: `${Math.round((row.c / total) * 100)}%` }}
                  />
                </div>
                <div className="w-20 text-right text-sm text-text-primary">{row.c}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-border-color grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-text-secondary">Отклонено античитом</div>
              <div className="text-text-primary font-medium">
                {(outcomes.rejections || []).length === 0
                  ? 'нет случаев'
                  : (outcomes.rejections || [])
                      .map((r) => `${REJECTION_LABELS[r.rejection_code] || r.rejection_code} — ${r.c}`)
                      .join(', ')}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-text-secondary">Зависших активных сессий</div>
                  <div className={`font-medium ${stale > 0 ? 'text-error' : 'text-text-primary'}`}>
                    {stale}
                  </div>
                </div>
                <button
                  onClick={onResetStale}
                  disabled={stale === 0 || isResetting}
                  title={
                    stale === 0
                      ? 'Зависших сессий нет'
                      : 'Перевести зависшие сессии в статус «Брошена»'
                  }
                  className="shrink-0 px-3 py-1.5 text-sm font-medium rounded-lg bg-bg-tertiary text-text-primary hover:bg-border-color disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {isResetting ? 'Сбрасываем...' : 'Сбросить в «Брошена»'}
                </button>
              </div>
              <div className="text-xs text-text-muted mt-1">
                Сессии со статусом «Идёт» старше времени жизни игры — их никто не закрывает
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================
// Вкладка
// ============================================

export const AnalyticsTab = () => {
  const { analytics, analyticsPeriod, isLoadingAnalytics, fetchAnalytics, resetStaleSessions } =
    useGamesStore();

  const [isResetting, setIsResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    fetchAnalytics(analyticsPeriod);
  }, [analyticsPeriod]);

  const handleResetStale = async () => {
    setConfirmReset(false);
    setIsResetting(true);
    try {
      await resetStaleSessions();
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="text-sm text-text-secondary">
          {isLoadingAnalytics ? 'Загрузка…' : `Период: ${analyticsPeriod === '7days' ? '7 дней' : analyticsPeriod === '90days' ? '90 дней' : '30 дней'}`}
        </div>
        <PeriodSelector
          period={analyticsPeriod}
          onChange={fetchAnalytics}
          disabled={isLoadingAnalytics}
        />
      </div>

      <SummaryCards summary={analytics.summary} />

      <TimeseriesChart series={analytics.series} />

      <ByGameTable games={analytics.byGame} />

      <OutcomesPanel
        outcomes={analytics.outcomes}
        onResetStale={() => setConfirmReset(true)}
        isResetting={isResetting}
      />

      {confirmReset && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setConfirmReset(false)}
        >
          <div
            className="bg-bg-secondary border border-border-color rounded-xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              Закрыть зависшие сессии?
            </h3>
            <p className="text-text-secondary text-sm mb-6">
              {analytics.outcomes?.stale_active || 0} сессий перейдут из «Идёт» в «Брошена».
              Затрагиваются только сессии, стартовавшие дольше времени жизни игры: партии,
              которые игроки ведут прямо сейчас, останутся нетронутыми.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmReset(false)}
                className="flex-1 py-2.5 px-4 bg-bg-tertiary hover:bg-bg-primary text-text-primary rounded-lg font-medium transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleResetStale}
                className="flex-1 py-2.5 px-4 bg-accent-primary hover:opacity-90 text-white rounded-lg font-medium transition-opacity"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
